import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  ShieldCheck, FileText, Ship, Globe, ArrowRight, CheckCircle2, Phone, Mail, 
  MapPin, Clock, Menu, X, ChevronRight, Award, Box, Building2, 
  Download, ExternalLink, Sliders, Check, AlertCircle, FileSpreadsheet, Lock
} from 'lucide-react';
import { companyInfo, productsList, exportProcessSteps, marketsServed, complianceList, faqsList } from './mock';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Header Navigation
export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Products", path: "/products" },
    { name: "Rapeseed/Mustard DOC", path: "/products/rapeseed-mustard-de-oiled-cake" },
    { name: "Export Process", path: "/export-process" },
    { name: "Markets", path: "/markets" },
    { name: "Compliance", path: "/certifications-compliance" },
    { name: "Contact & Quote", path: "/contact" }
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#183D2F] text-white shadow-md border-b border-[#2F5D46]">
      {/* Top Bar */}
      <div className="bg-[#143226] text-xs py-2 px-4 sm:px-8 border-b border-[#2F5D46]/40 flex flex-wrap justify-between items-center gap-2">
        <div className="flex items-center gap-6">
          <span className="flex items-center gap-1.5" data-testid="topbar-location">
            <MapPin className="w-3.5 h-3.5 text-[#C6922E]" />
            Jaipur, Rajasthan, India
          </span>
          <span className="hidden md:flex items-center gap-1.5" data-testid="topbar-email">
            <Mail className="w-3.5 h-3.5 text-[#C6922E]" />
            {companyInfo.email}
          </span>
        </div>
        <div className="flex items-center gap-6">
          <span className="flex items-center gap-1.5" data-testid="topbar-phone-1">
            <Phone className="w-3.5 h-3.5 text-[#C6922E]" />
            Puneet: {companyInfo.founders[0].phone}
          </span>
          <span className="hidden sm:flex items-center gap-1.5" data-testid="topbar-phone-2">
            <Phone className="w-3.5 h-3.5 text-[#C6922E]" />
            Sushant: {companyInfo.founders[1].phone}
          </span>
          <Link to="/admin" className="text-[#C6922E] hover:underline flex items-center gap-1 font-medium" data-testid="admin-portal-link">
            <Lock className="w-3 h-3" /> Admin
          </Link>
        </div>
      </div>

      {/* Main Nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3.5 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3.5" data-testid="brand-logo-link">
          <div className="bg-white p-1.5 rounded shadow-md flex items-center justify-center shrink-0 w-12 h-12">
            <img 
              src="https://customer-assets-jai6qajn.emergentagent.net/job_verified-doc-supply/artifacts/dnzw6tul_logo.webp" 
              alt="Axora PS Global LLP Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 className="font-bold text-base sm:text-lg tracking-tight text-white leading-tight">AXORA PS GLOBAL LLP</h1>
            <p className="text-[11px] text-[#EFE6D0]/80 tracking-widest uppercase">Merchant Exporter • India</p>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1.5">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/[\/\s]+/g, '-')}`}
                className={`px-2.5 py-2 rounded text-xs xl:text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-[#2F5D46] text-[#EFE6D0] border-b-2 border-[#C6922E]' 
                    : 'text-[#EFE6D0]/90 hover:bg-[#2F5D46]/60 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            );
          })}
        </nav>

        {/* CTA Button */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-request-quote-btn"
            className="bg-[#C6922E] hover:bg-[#b08026] text-[#183D2F] px-4 py-2 rounded font-semibold text-sm shadow transition-all transform hover:-translate-y-0.5"
          >
            Request a Quote
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden p-2 text-white hover:bg-[#2F5D46] rounded focus:outline-none"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#143226] border-t border-[#2F5D46] px-4 py-4 space-y-2">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded text-sm font-medium text-white hover:bg-[#2F5D46]"
              data-testid={`mobile-nav-${link.name.toLowerCase().replace(/[\/\s]+/g, '-')}`}
            >
              {link.name}
            </Link>
          ))}
          <div className="pt-2">
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-center bg-[#C6922E] text-[#183D2F] font-semibold py-2.5 rounded text-sm"
              data-testid="mobile-request-quote-btn"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

// Footer Component
export function Footer() {
  return (
    <footer className="bg-[#122B21] text-[#EFE6D0] pt-16 pb-12 border-t border-[#2F5D46]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
        {/* Company Intro */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="bg-white p-1 rounded shadow shrink-0 w-10 h-10 flex items-center justify-center">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_verified-doc-supply/artifacts/dnzw6tul_logo.webp" 
                alt="Axora PS Global LLP Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="font-bold text-lg text-white tracking-tight">AXORA PS GLOBAL LLP</h3>
          </div>
          <p className="text-sm text-[#EFE6D0]/80 leading-relaxed">
            Reliable Indian agri-commodity merchant exporter based in Jaipur, Rajasthan. Sourcing quality-verified rapeseed/mustard de-oiled cake and feed ingredients from trusted partner processors, shipped globally.
          </p>
          <div className="pt-2 space-y-1 text-xs">
            <p className="text-[#C6922E] font-semibold">GST: {companyInfo.gstNumber}</p>
            <span className="inline-block bg-[#1E4D2B] text-[#C6922E] text-xs font-semibold px-2.5 py-1 rounded border border-[#C6922E]/30 mt-1" data-testid="footer-model-badge">
              Merchant Exporter LLP Model
            </span>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-white font-semibold text-base mb-4 border-b border-[#2F5D46] pb-2">Quick Navigation</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/" className="hover:text-[#C6922E] transition-colors">Home</Link></li>
            <li><Link to="/about" className="hover:text-[#C6922E] transition-colors">About Us</Link></li>
            <li><Link to="/products" className="hover:text-[#C6922E] transition-colors">Products Catalog</Link></li>
            <li><Link to="/products/rapeseed-mustard-de-oiled-cake" className="hover:text-[#C6922E] transition-colors">Rapeseed/Mustard DOC</Link></li>
            <li><Link to="/export-process" className="hover:text-[#C6922E] transition-colors">Export Process & Logistics</Link></li>
            <li><Link to="/markets" className="hover:text-[#C6922E] transition-colors">Markets Served (Global)</Link></li>
            <li><Link to="/certifications-compliance" className="hover:text-[#C6922E] transition-colors">Compliance & GST</Link></li>
            <li><Link to="/contact" className="hover:text-[#C6922E] transition-colors">Request a Quote / Contact</Link></li>
          </ul>
        </div>

        {/* Office & Contact */}
        <div className="space-y-3 text-sm">
          <h4 className="text-white font-semibold text-base mb-4 border-b border-[#2F5D46] pb-2">Jaipur Corporate Office</h4>
          <p className="flex items-start gap-2.5 text-[#EFE6D0]/90" data-testid="footer-address">
            <MapPin className="w-4 h-4 text-[#C6922E] shrink-0 mt-0.5" />
            <span>{companyInfo.address}</span>
          </p>
          <p className="flex items-center gap-2.5 text-[#EFE6D0]/90" data-testid="footer-email">
            <Mail className="w-4 h-4 text-[#C6922E] shrink-0" />
            <a href={`mailto:${companyInfo.email}`} className="hover:underline">{companyInfo.email}</a>
          </p>
          <p className="flex items-center gap-2.5 text-[#EFE6D0]/90" data-testid="footer-hours">
            <Clock className="w-4 h-4 text-[#C6922E] shrink-0" />
            <span>{companyInfo.businessHours}</span>
          </p>
          <div className="pt-2 space-y-1 text-xs text-[#EFE6D0]/80">
            <p className="font-semibold text-white">Co-founders:</p>
            <p>Puneet Aggarwal: <a href={`tel:${companyInfo.founders[0].phone}`} className="text-[#C6922E] hover:underline">{companyInfo.founders[0].phone}</a></p>
            <p>Sushant Lohia: <a href={`tel:${companyInfo.founders[1].phone}`} className="text-[#C6922E] hover:underline">{companyInfo.founders[1].phone}</a></p>
          </div>
        </div>

        {/* Trade Highlights */}
        <div className="space-y-4">
          <h4 className="text-white font-semibold text-base mb-4 border-b border-[#2F5D46] pb-2">Export Ports & Logistics</h4>
          <div className="space-y-2 text-xs text-[#EFE6D0]/90">
            <div className="bg-[#183D2F] p-3 rounded border border-[#2F5D46]">
              <p className="font-semibold text-white mb-1">Export Ports:</p>
              <p>Mundra, Kandla, Nhava Sheva (JNPT), Pipavav, Hazira</p>
            </div>
            <div className="bg-[#183D2F] p-3 rounded border border-[#2F5D46]">
              <p className="font-semibold text-white mb-1">Global Reach:</p>
              <p>Asia, Middle East, Africa, Europe & Key Feed Markets</p>
            </div>
            <div className="bg-[#183D2F] p-3 rounded border border-[#2F5D46]">
              <p className="font-semibold text-white mb-1">Quality Testing:</p>
              <p>Batch-wise COA & lab test support on request</p>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Disclaimer Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-8 border-t border-[#2F5D46]/60 text-xs text-[#EFE6D0]/70 space-y-3">
        <p className="leading-relaxed">
          <strong className="text-[#EFE6D0]">Compliance Disclaimer:</strong> Product specifications, documentation, shipment terms, and availability are subject to final contract, buyer requirements, batch-wise quality results, and applicable export/import regulations. HSN codes are provided for buyer reference only and should be verified against the final product form and customs classification.
        </p>
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-2">
          <p>© {new Date().getFullYear()} Axora PS Global LLP. GST: {companyInfo.gstNumber}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/certifications-compliance" className="hover:text-[#C6922E]">Compliance & GST</Link>
            <span>•</span>
            <Link to="/contact" className="hover:text-[#C6922E]">Inquiries</Link>
            <span>•</span>
            <Link to="/admin" className="hover:text-[#C6922E]">Admin Portal</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Home Page
export function HomePage() {
  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      {/* Hero Section */}
      <section className="relative bg-[#183D2F] text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1593617761943-9099951a0769?crop=entropy&cs=srgb&fm=jpg&q=85')` }}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 bg-[#2F5D46] px-3.5 py-1.5 rounded-full text-xs font-semibold text-[#EFE6D0] border border-[#C6922E]/40" data-testid="hero-badge">
              <ShieldCheck className="w-4 h-4 text-[#C6922E]" />
              Merchant Exporter LLP • Jaipur, Rajasthan
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
              Rapeseed/Mustard DOC Exporter <span className="text-[#C6922E]">from India</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#EFE6D0]/90 leading-relaxed max-w-2xl">
              Merchant export of quality-verified rapeseed/mustard de-oiled cake from India for global feed mills, commodity importers, and trading companies.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/contact"
                data-testid="hero-request-quote-btn"
                className="bg-[#C6922E] hover:bg-[#b08026] text-[#183D2F] font-bold px-7 py-3.5 rounded shadow-lg transition-all flex items-center gap-2 text-base"
              >
                Request a Quote <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/products/rapeseed-mustard-de-oiled-cake"
                data-testid="hero-view-specs-btn"
                className="bg-transparent hover:bg-white/10 text-white font-semibold px-7 py-3.5 rounded border border-white/30 transition-all text-base"
              >
                View Product Specs
              </Link>
            </div>

            {/* Hero Trust Badges Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-10 border-t border-white/10">
              {[
                "Axora PS Global LLP",
                "Verified partner processor sourcing",
                "Batch-wise COA and test support",
                "FOB / CFR / CIF options",
                "Shipments via suitable Indian ports",
                "Global buyer inquiry support"
              ].map((badge, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-[#EFE6D0]">
                  <CheckCircle2 className="w-4 h-4 text-[#C6922E] shrink-0" />
                  <span>{badge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Short Introduction */}
      <section className="py-16 sm:py-24 max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2F5D46] bg-[#2F5D46]/10 px-3 py-1 rounded">
              Corporate Profile
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#183D2F] tracking-tight">
              Dependable Agri-Commodity Merchant Exporter from Rajasthan
            </h2>
            <p className="text-base sm:text-lg text-[#1F2933]/80 leading-relaxed">
              Axora PS Global LLP is a Jaipur, Rajasthan based merchant exporter specializing in dependable agri-commodity exports from India. We work with verified partner factories and processors to source export-suitable rapeseed/mustard de-oiled cake (DOC) / rapeseed-mustard meal, coordinate batch-wise quality checks, prepare export documentation, and support shipments through suitable Indian ports.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="bg-white p-5 rounded-lg border border-[#D8DDD5] shadow-sm">
                <h3 className="font-bold text-[#183D2F] mb-1 text-base">Verified Sourcing</h3>
                <p className="text-sm text-[#4B5563]">Sourced from established partner processors in Rajasthan (including Niwai hub).</p>
              </div>
              <div className="bg-white p-5 rounded-lg border border-[#D8DDD5] shadow-sm">
                <h3 className="font-bold text-[#183D2F] mb-1 text-base">Flexible Port Logistics</h3>
                <p className="text-sm text-[#4B5563]">Shipments coordinated via Mundra, Kandla, JNPT, Pipavav, and Hazira ports.</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-[#183D2F] p-8 rounded-2xl text-white shadow-xl space-y-6 border border-[#2F5D46]">
              <div className="flex items-center gap-3 border-b border-[#2F5D46] pb-4">
                <div className="bg-white p-1 rounded shrink-0 w-10 h-10 flex items-center justify-center">
                  <img src="https://customer-assets-jai6qajn.emergentagent.net/job_verified-doc-supply/artifacts/dnzw6tul_logo.webp" alt="Logo" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Axora PS Global LLP</h3>
                  <p className="text-xs text-[#EFE6D0]/70">Merchant Exporter • Rajasthan, India</p>
                </div>
              </div>
              <ul className="space-y-4 text-sm text-[#EFE6D0]/90">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#C6922E] mt-1.5 shrink-0"></span>
                  <span><strong>Primary Product:</strong> Rapeseed/Mustard De-Oiled Cake (DOC) / Rapeseed-Mustard Meal</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#C6922E] mt-1.5 shrink-0"></span>
                  <span><strong>Global Markets:</strong> Feed mills, traders & importers across Asia, Middle East, Africa & Europe</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#C6922E] mt-1.5 shrink-0"></span>
                  <span><strong>Quality Testing:</strong> Batch COA & laboratory test support on request</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#C6922E] mt-1.5 shrink-0"></span>
                  <span><strong>Founders:</strong> Puneet Aggarwal & Sushant Lohia</span>
                </li>
              </ul>
              <div className="pt-2">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 text-[#C6922E] hover:text-white font-semibold text-sm transition-colors"
                >
                  Read full corporate overview <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Product */}
      <section className="bg-white py-20 border-t border-b border-[#D8DDD5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#2F5D46] bg-[#2F5D46]/10 px-3 py-1 rounded">
                Flagship Offering
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#183D2F] mt-3">Featured Agri-Commodity</h2>
            </div>
            <Link
              to="/products"
              className="mt-4 md:mt-0 text-[#183D2F] font-semibold flex items-center gap-1 hover:text-[#C6922E] transition-colors"
            >
              View all products catalog <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-[#F8F7F2] rounded-2xl p-6 sm:p-10 border border-[#D8DDD5]">
            <div className="lg:col-span-5 space-y-6">
              <span className="inline-block bg-[#C6922E]/20 text-[#183D2F] font-bold text-xs px-3 py-1 rounded">
                HSN 23069012 / 23069021
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#183D2F]">Rapeseed/Mustard De-Oiled Cake (DOC)</h3>
              <p className="text-sm sm:text-base text-[#1F2933]/80 leading-relaxed">
                Protein-rich oilseed meal / rapeseed-mustard meal sourced from verified Rajasthan partner processors for global animal feed and commodity buyers. Sourced with strict quality oversight, moisture control, and batch-wise COA verification.
              </p>
              
              <div className="space-y-2.5 text-sm text-[#1F2933]">
                <div className="flex justify-between py-1.5 border-b border-[#D8DDD5]">
                  <span className="text-[#4B5563]">Crude Protein:</span>
                  <span className="font-semibold text-[#183D2F]">36-38% min typical</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[#D8DDD5]">
                  <span className="text-[#4B5563]">Moisture:</span>
                  <span className="font-semibold text-[#183D2F]">10-12% max typical</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[#D8DDD5]">
                  <span className="text-[#4B5563]">Sand & Silica:</span>
                  <span className="font-semibold text-[#183D2F]">2% max</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[#D8DDD5]">
                  <span className="text-[#4B5563]">Origin & Ports:</span>
                  <span className="font-semibold text-[#183D2F]">Rajasthan / Indian Ports</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <Link
                  to="/products/rapeseed-mustard-de-oiled-cake"
                  data-testid="featured-view-specs-btn"
                  className="bg-[#183D2F] hover:bg-[#143226] text-white font-semibold px-6 py-3 rounded text-sm transition-all shadow"
                >
                  View Full Specifications & COA
                </Link>
                <Link
                  to="/contact"
                  data-testid="featured-request-quote-btn"
                  className="bg-[#C6922E] hover:bg-[#b08026] text-[#183D2F] font-semibold px-6 py-3 rounded text-sm transition-all shadow"
                >
                  Request a Quote
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="relative rounded-xl overflow-hidden shadow-lg bg-white border border-[#D8DDD5]">
                <img 
                  src="https://images.unsplash.com/photo-1593617761943-9099951a0769?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Rapeseed mustard de-oiled cake packed in export bags for container loading" 
                  className="w-full h-80 sm:h-96 object-cover"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
                  <p className="text-xs uppercase tracking-wider text-[#C6922E] font-semibold">Quality Verified Commodity</p>
                  <p className="text-lg font-bold">Packed in 50 kg PP/HDPE Bags • 22-24 MT 20ft Container Load</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Axora PS Global */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-[#2F5D46] bg-[#2F5D46]/10 px-3 py-1 rounded">
            Our Core Strengths
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#183D2F]">Why Choose Axora PS Global LLP</h2>
          <p className="text-sm sm:text-base text-[#1F2933]/70">
            Built for international feed mills, trading companies, and procurement directors who value reliability, documentation discipline, and compliance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              title: "Verified Partner Sourcing",
              desc: "We work directly with established partner factories and processors in Rajasthan to ensure consistent agricultural quality.",
              icon: Building2
            },
            {
              title: "Batch-Wise COA & Lab Support",
              desc: "Every shipment is supported by rigorous laboratory test reports and batch-wise Certificate of Analysis.",
              icon: ShieldCheck
            },
            {
              title: "Flexible Port Logistics",
              desc: "Coordination through Mundra, Kandla, JNPT, Pipavav, and Hazira ports with reliable container stuffing.",
              icon: Ship
            },
            {
              title: "Documentation Discipline",
              desc: "Comprehensive export documentation including commercial invoices, packing lists, certificate of origin, and phytosanitary docs.",
              icon: FileText
            },
            {
              title: "Global Buyer Reach",
              desc: "Serving feed mills, commodity importers, and trading companies across Asia, Middle East, Africa, and Europe.",
              icon: Globe
            },
            {
              title: "Clear Buyer Communication",
              desc: "Direct communication channels with co-founders Puneet Aggarwal and Sushant Lohia from inquiry to post-shipment delivery.",
              icon: Phone
            }
          ].map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={idx} className="bg-white p-8 rounded-xl border border-[#D8DDD5] shadow-sm hover:shadow-md transition-shadow space-y-4">
                <div className="w-12 h-12 rounded-lg bg-[#183D2F]/10 text-[#183D2F] flex items-center justify-center font-bold">
                  <Icon className="w-6 h-6 text-[#183D2F]" />
                </div>
                <h3 className="text-xl font-bold text-[#183D2F]">{item.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Export Process Preview */}
      <section className="bg-[#183D2F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16">
            <div className="space-y-3">
              <span className="text-xs font-bold uppercase tracking-widest text-[#C6922E] bg-[#2F5D46] px-3 py-1 rounded">
                Seamless Logistics
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold">Our Export Process</h2>
            </div>
            <Link
              to="/export-process"
              className="mt-4 md:mt-0 text-[#C6922E] hover:underline font-semibold flex items-center gap-1 text-sm"
            >
              View detailed 8-step export timeline <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {exportProcessSteps.slice(0, 4).map((s) => (
              <div key={s.step} className="bg-[#143226] p-6 rounded-xl border border-[#2F5D46] space-y-3">
                <span className="text-xs font-bold text-[#C6922E]">STEP 0{s.step}</span>
                <h3 className="font-bold text-lg text-white">{s.title}</h3>
                <p className="text-xs text-[#EFE6D0]/80 leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 max-w-5xl mx-auto px-4 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-[#2F5D46] bg-[#2F5D46]/10 px-3 py-1 rounded">
            Buyer Guidance
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#183D2F]">Frequently Asked Questions</h2>
          <p className="text-sm text-[#4B5563]">Common inquiries from international feed mills, traders, and procurement teams.</p>
        </div>

        <div className="space-y-4">
          {faqsList.slice(0, 6).map((faq, idx) => (
            <div key={idx} className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-2">
              <h3 className="font-bold text-base text-[#183D2F] flex items-start gap-2">
                <span className="text-[#C6922E]">Q:</span> {faq.q}
              </h3>
              <p className="text-sm text-[#4B5563] pl-5 leading-relaxed">
                <strong className="text-[#183D2F]">A:</strong> {faq.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Quote CTA Strip */}
      <section className="bg-[#C6922E] py-16 text-[#183D2F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
          <div className="space-y-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Ready to Source Quality Rapeseed/Mustard DOC from India?</h2>
            <p className="text-sm sm:text-base font-medium opacity-90">Contact our Jaipur export team for batch COA, FOB / CFR pricing, and container schedules.</p>
          </div>
          <Link
            to="/contact"
            data-testid="bottom-cta-request-quote"
            className="bg-[#183D2F] hover:bg-[#143226] text-white font-bold px-8 py-4 rounded-lg shadow-lg text-base transition-all shrink-0"
          >
            Request a Quote Now
          </Link>
        </div>
      </section>
    </div>
  );
}

// About Us Page
export function AboutPage() {
  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      {/* Header Banner */}
      <section className="bg-[#183D2F] text-white py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#2F5D46] px-3 py-1 rounded text-xs font-semibold text-[#EFE6D0]">
            Axora PS Global LLP • GST: {companyInfo.gstNumber}
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">About Axora PS Global LLP</h1>
          <p className="text-base sm:text-lg text-[#EFE6D0]/90 max-w-3xl leading-relaxed">
            A compliance-first merchant exporter built for global B2B buyers who need consistent sourcing, proper documentation, shipment discipline, and clear communication.
          </p>
        </div>
      </section>

      {/* Main Corporate Overview */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-3xl font-bold text-[#183D2F]">Our Positioning & Heritage</h2>
            <p className="text-base text-[#1F2933]/80 leading-relaxed">
              Based in Jaipur, Rajasthan, Axora PS Global LLP operates on a robust merchant exporter model. We do not manufacture products directly. Instead, we maintain close access to major mustard and rapeseed sourcing and processing regions across Rajasthan, allowing us to source export-suitable rapeseed/mustard de-oiled cake (DOC) with high reliability.
            </p>
            <p className="text-base text-[#1F2933]/80 leading-relaxed">
              Our global focus is serving animal feed mills, commodity traders, and livestock feed manufacturers across Asia, the Middle East, Africa, and Europe. We coordinate batch-wise quality checks, prepare compliance documentation, and oversee container stuffing and dispatch through suitable Indian ports.
            </p>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="bg-white p-4 rounded border border-[#D8DDD5]">
                <p className="text-xs text-[#4B5563]">Primary Sourcing</p>
                <p className="font-bold text-[#183D2F] text-base">Rajasthan Partner Processors</p>
              </div>
              <div className="bg-white p-4 rounded border border-[#D8DDD5]">
                <p className="text-xs text-[#4B5563]">Export Gateways</p>
                <p className="font-bold text-[#183D2F] text-base">Mundra, Kandla, JNPT & Pipavav</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 bg-white p-8 rounded-2xl border border-[#D8DDD5] shadow-sm space-y-6">
            <h3 className="font-bold text-xl text-[#183D2F] border-b border-[#D8DDD5] pb-3">Corporate Mission & Vision</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-sm uppercase tracking-wider text-[#C6922E]">Mission</h4>
                <p className="text-sm text-[#4B5563] mt-1">To make Indian agri-commodity sourcing simpler, more transparent, and more reliable for global B2B buyers.</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm uppercase tracking-wider text-[#C6922E]">Vision</h4>
                <p className="text-sm text-[#4B5563] mt-1">To become a trusted Indian merchant export partner for feed raw materials and oilseed meals across international markets.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#183D2F] text-center mb-10">Our Core Values</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Reliability", desc: "Consistent execution from inquiry placement to vessel dispatch." },
              { title: "Compliance", desc: "Strict adherence to export-import regulations, GST ({companyInfo.gstNumber}), and customs protocols." },
              { title: "Quality Verification", desc: "Batch-wise COA coordination and verified partner factory sourcing." },
              { title: "Clear Communication", desc: "Transparent, direct dialogue with co-founders at every shipment stage." },
              { title: "Responsible Sourcing", desc: "Partnering with audited and certified processing units in Rajasthan." },
              { title: "Long-Term Relationships", desc: "Building sustainable trade bridges with global feed millers and traders." }
            ].map((v, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-2">
                <h3 className="font-bold text-lg text-[#183D2F]">{v.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Leadership Section */}
        <div className="bg-white rounded-2xl p-8 sm:p-12 border border-[#D8DDD5] shadow-sm">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2F5D46] bg-[#2F5D46]/10 px-3 py-1 rounded">
              Executive Leadership
            </span>
            <h2 className="text-3xl font-bold text-[#183D2F]">Co-founders & Direct Contacts</h2>
            <p className="text-sm text-[#4B5563]">Direct management oversight for all international procurement inquiries and shipments.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {companyInfo.founders.map((f, idx) => (
              <div key={idx} className="bg-[#F8F7F2] p-8 rounded-xl border border-[#D8DDD5] space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#183D2F] text-white flex items-center justify-center font-bold text-xl">
                    {f.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-[#183D2F]">{f.name}</h3>
                    <p className="text-xs font-semibold text-[#C6922E] uppercase tracking-wider">{f.role} • Axora PS Global LLP</p>
                  </div>
                </div>
                <div className="space-y-2 pt-2 text-sm text-[#4B5563]">
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#183D2F]" />
                    <span>Phone / WhatsApp: <a href={`tel:${f.phone}`} className="font-semibold text-[#183D2F] hover:underline">{f.phone}</a></span>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#183D2F]" />
                    <span>Email: <a href={`mailto:${companyInfo.email}`} className="font-semibold text-[#183D2F] hover:underline">{companyInfo.email}</a></span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// Shared hook — fetches products from backend, falls back to mock data on error
// Merges mock image assets so backend data still renders nicely.
function mergeProductImages(list) {
  return list.map(p => {
    const mock = productsList.find(m => m.slug === p.slug);
    return {
      ...p,
      image: p.image || mock?.image,
      bannerImage: p.bannerImage || mock?.bannerImage,
    };
  });
}

function useProducts() {
  const [products, setProducts] = React.useState(productsList);
  React.useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}/products`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (mounted && Array.isArray(data) && data.length) setProducts(mergeProductImages(data)); })
      .catch(() => { /* fallback to mock */ });
    return () => { mounted = false; };
  }, []);
  return products;
}

function useProduct(slug) {
  const [product, setProduct] = React.useState(() => productsList.find(p => p.slug === slug));
  React.useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}/products/${slug}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (mounted && data) setProduct(mergeProductImages([data])[0]); })
      .catch(() => { /* fallback to mock */ });
    return () => { mounted = false; };
  }, [slug]);
  return product;
}

// Products Page (CMS Style Catalog)
export function ProductsPage() {
  const products = useProducts();
  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      <section className="bg-[#183D2F] text-white py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#2F5D46] px-3 py-1 rounded text-xs font-semibold text-[#EFE6D0]">
            Export Product Catalog
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Agri-Commodities & Feed Ingredients</h1>
          <p className="text-base sm:text-lg text-[#EFE6D0]/90 max-w-3xl leading-relaxed">
            Sourced from verified Rajasthan partner processors, quality-verified, and shipped in 20ft containers via suitable Indian ports.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-8 space-y-12">
        {/* Active Products */}
        <div>
          <h2 className="text-2xl font-bold text-[#183D2F] mb-6 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-600"></span> Primary Export Product
          </h2>

          <div className="grid grid-cols-1 gap-8">
            {products.filter(p => p.status === 'active').map(product => (
              <div key={product.id} className="bg-white rounded-2xl border border-[#D8DDD5] shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-4 relative min-h-[250px] bg-slate-100 flex items-center justify-center p-6">
                  <img src={product.bannerImage || product.image} alt={product.name} className="w-full h-full object-cover rounded" />
                  <div className="absolute top-4 left-4 bg-[#183D2F] text-white text-xs font-bold px-3 py-1 rounded">
                    Active Export
                  </div>
                </div>
                <div className="lg:col-span-8 p-6 sm:p-8 space-y-6 flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {product.tags.map((t, idx) => (
                        <span key={idx} className="bg-[#F8F7F2] border border-[#D8DDD5] text-[#183D2F] text-xs font-medium px-2.5 py-1 rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-[#183D2F]">{product.name}</h3>
                    <p className="text-sm text-[#4B5563] leading-relaxed">{product.subtitle}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 text-xs text-[#4B5563]">
                      <div className="bg-[#F8F7F2] p-3 rounded">
                        <span className="font-semibold text-[#183D2F]">Origin:</span> {product.origin}
                      </div>
                      <div className="bg-[#F8F7F2] p-3 rounded">
                        <span className="font-semibold text-[#183D2F]">Export Port:</span> {product.exportPort}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 pt-4 border-t border-[#D8DDD5]">
                    <Link
                      to={`/products/${product.slug}`}
                      data-testid={`product-specs-${product.slug}`}
                      className="bg-[#183D2F] hover:bg-[#143226] text-white font-semibold px-6 py-2.5 rounded text-sm transition-all"
                    >
                      View Specifications & COA
                    </Link>
                    <Link
                      to="/contact"
                      data-testid={`product-quote-${product.slug}`}
                      className="bg-[#C6922E] hover:bg-[#b08026] text-[#183D2F] font-semibold px-6 py-2.5 rounded text-sm transition-all"
                    >
                      Request Quote
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Future Products Placeholder Cards */}
        <div>
          <h2 className="text-2xl font-bold text-[#183D2F] mb-6 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-amber-500"></span> Upcoming & Pipeline Agri-Commodities
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.filter(p => p.status === 'coming_soon').map(product => (
              <div key={product.id} className="bg-white rounded-xl border border-[#D8DDD5] shadow-sm p-6 space-y-4 relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-4 right-4 bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1 rounded">
                  Coming Soon
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-[#183D2F] pr-20">{product.name}</h3>
                  <p className="text-xs text-[#4B5563] leading-relaxed">{product.subtitle}</p>
                  <div className="text-xs text-[#4B5563] space-y-1 pt-2">
                    <p><strong>Origin:</strong> {product.origin}</p>
                    <p><strong>Port:</strong> {product.exportPort}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-[#D8DDD5]">
                  <Link
                    to="/contact"
                    className="block text-center bg-[#F8F7F2] hover:bg-[#183D2F] hover:text-white text-[#183D2F] border border-[#D8DDD5] font-semibold py-2 rounded text-xs transition-colors"
                  >
                    Available on Inquiry
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// Mustard DOC Product Detail Page
export function MustardDocDetailPage() {
  const product = useProduct('rapeseed-mustard-de-oiled-cake');
  const pdfUrl = `${API_BASE}/products/rapeseed-mustard-de-oiled-cake/spec-sheet.pdf`;

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      <section className="bg-[#183D2F] text-white py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#2F5D46] px-3 py-1 rounded text-xs font-semibold text-[#EFE6D0]">
            Flagship Export Product • HSN 23069012 / 23069021
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Rapeseed/Mustard De-Oiled Cake (DOC)</h1>
          <p className="text-base sm:text-lg text-[#EFE6D0]/90 max-w-3xl leading-relaxed">
            Sourced from verified Rajasthan partner processors, batch-wise COA supported, and shipped in 20ft containers via suitable Indian ports for global feed markets.
          </p>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-8 space-y-16">
        {/* Overview & Trade Names */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#183D2F]">Product Overview & Trade Names</h2>
            <p className="text-base text-[#1F2933]/80 leading-relaxed">
              Rapeseed/Mustard De-Oiled Cake, also known as Rapeseed/Mustard DOC, Rapeseed-Mustard Meal, Rapeseed/Mustard Meal, or Mustard/Rapeseed Oil Cake, is an oilseed meal used as an animal feed raw material by feed mills, importers, commodity traders, and livestock feed manufacturers. Axora PS Global LLP sources this commodity from verified partner processors in Rajasthan and coordinates batch-wise quality checks, export documentation, and shipment through Indian ports.
            </p>

            <div className="bg-white p-6 rounded-xl border border-[#D8DDD5] space-y-3">
              <h3 className="font-bold text-base text-[#183D2F]">Common Trade Names:</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-[#4B5563]">
                {["Rapeseed/Mustard DOC", "Rapeseed-Mustard Meal", "Rapeseed/Mustard Meal", "Mustard/Rapeseed Oil Cake", "De-Oiled Cake Meal", "Oilseed Meal"].map((name, i) => (
                  <div key={i} className="bg-[#F8F7F2] p-2.5 rounded border border-[#D8DDD5] font-medium text-[#183D2F]">
                    • {name}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-xl border border-[#D8DDD5]">
                <h3 className="font-bold text-[#183D2F] mb-2 text-sm uppercase tracking-wider text-[#C6922E]">Origin & Sourcing</h3>
                <p className="text-sm text-[#4B5563]">Rajasthan, India (Sourced from verified partner factories and processors, including Niwai sourcing region).</p>
              </div>
              <div className="bg-white p-5 rounded-xl border border-[#D8DDD5]">
                <h3 className="font-bold text-[#183D2F] mb-2 text-sm uppercase tracking-wider text-[#C6922E]">Export Ports & Markets</h3>
                <p className="text-sm text-[#4B5563]">Mundra, Kandla, JNPT, Pipavav & Hazira ports. Global buyer reach across Asia, Middle East, Africa & Europe.</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#D8DDD5] shadow-sm space-y-4">
              <h3 className="font-bold text-lg text-[#183D2F] border-b border-[#D8DDD5] pb-3">Quick Trade Summary</h3>
              <ul className="space-y-3 text-sm text-[#4B5563]">
                <li className="flex justify-between"><strong className="text-[#183D2F]">HSN Codes:</strong> <span>23069012 / 23069021</span></li>
                <li className="flex justify-between"><strong className="text-[#183D2F]">Container Load:</strong> <span>22-24 MT (20ft dry container)</span></li>
                <li className="flex justify-between"><strong className="text-[#183D2F]">Standard Packing:</strong> <span>50 kg PP / HDPE bags</span></li>
                <li className="flex justify-between"><strong className="text-[#183D2F]">Shipment Terms:</strong> <span>FOB Indian Port / CFR / CIF</span></li>
                <li className="flex justify-between"><strong className="text-[#183D2F]">Payment Terms:</strong> <span>LC at sight / TT Wire</span></li>
                <li className="flex justify-between"><strong className="text-[#183D2F]">Lead Time:</strong> <span>7-15 working days</span></li>
              </ul>
              <div className="pt-4 space-y-2">
                <Link
                  to="/contact"
                  data-testid="detail-request-quote-btn"
                  className="block text-center bg-[#C6922E] hover:bg-[#b08026] text-[#183D2F] font-bold py-3 rounded shadow transition-all text-sm"
                >
                  Request Quotation for Rapeseed/Mustard DOC
                </Link>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="detail-download-pdf-btn"
                  className="flex items-center justify-center gap-2 bg-white hover:bg-[#F8F7F2] text-[#183D2F] border border-[#183D2F] font-semibold py-3 rounded shadow-sm transition-all text-sm"
                >
                  <Download className="w-4 h-4" /> Download Spec Sheet (PDF)
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Indicative Product Specification Table */}
        <div className="bg-white rounded-2xl border border-[#D8DDD5] shadow-sm overflow-hidden">
          <div className="bg-[#183D2F] text-white p-6 sm:p-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <span className="text-xs font-bold text-[#C6922E] uppercase tracking-wider">Quality Assurance & Lab Support</span>
              <h2 className="text-2xl font-bold">Indicative Product Specification Table</h2>
            </div>
            <span className="bg-[#2F5D46] text-[#EFE6D0] text-xs px-3 py-1.5 rounded border border-[#C6922E]/40">
              Batch-Specific & COA Supported
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-[#F8F7F2] text-[#183D2F] border-b border-[#D8DDD5]">
                  <th className="p-4 font-bold">Parameter</th>
                  <th className="p-4 font-bold">Typical / Indicative Value</th>
                  <th className="p-4 font-bold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D8DDD5]">
                {product.specifications.map((spec, i) => (
                  <tr key={i} className="hover:bg-[#F8F7F2]/50 transition-colors">
                    <td className="p-4 font-semibold text-[#183D2F]">{spec.parameter}</td>
                    <td className="p-4 text-[#1F2933]">{spec.typical}</td>
                    <td className="p-4 text-[#4B5563] text-xs italic">{spec.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-6 bg-[#F8F7F2] border-t border-[#D8DDD5] text-xs text-[#4B5563] space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#C6922E] shrink-0 mt-0.5" />
              <p>
                <strong>Specification Note:</strong> Specification values are batch-specific and can be supported by Certificate of Analysis and relevant quality test reports where required by the buyer. HSN codes are provided for buyer reference only.
              </p>
            </div>
            <p className="pl-6 text-[#183D2F] font-medium">
              Certification and testing support available on request for: Crude Protein, Moisture, Residual Oil/Fat, Sand & Silica (2% max), Crude Fiber, Ash, Foreign Matter, Aflatoxin, Salmonella/Microbiology, Heavy Metals, and Pesticide Residue subject to destination requirements and lab availability.
            </p>
          </div>
        </div>

        {/* Grades, Packaging, Logistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-4">
            <h3 className="font-bold text-lg text-[#183D2F] border-b border-[#D8DDD5] pb-2">Grades Available</h3>
            <ul className="space-y-2 text-sm text-[#4B5563]">
              {product.grades.map((g, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#C6922E]" /> {g}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-4">
            <h3 className="font-bold text-lg text-[#183D2F] border-b border-[#D8DDD5] pb-2">Packaging & Load</h3>
            <div className="space-y-3 text-sm text-[#4B5563]">
              <p><strong>Packaging:</strong> {product.packaging}</p>
              <p><strong>Container Load:</strong> {product.containerLoad}</p>
              <p><strong>Shelf Life:</strong> {product.shelfLife}</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-4">
            <h3 className="font-bold text-lg text-[#183D2F] border-b border-[#D8DDD5] pb-2">Quality & Documents</h3>
            <div className="space-y-2 text-xs text-[#4B5563]">
              <p className="font-semibold text-[#183D2F]">Key Checks:</p>
              <p>{product.qualityChecks.join(', ')}</p>
              <p className="font-semibold text-[#183D2F] pt-2">Available Docs:</p>
              <p>{product.documents.join(', ')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Export Process Page
export function ExportProcessPage() {
  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      <section className="bg-[#183D2F] text-white py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#2F5D46] px-3 py-1 rounded text-xs font-semibold text-[#EFE6D0]">
            Logistics & Execution Timeline
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Export Process & Port Logistics</h1>
          <p className="text-base sm:text-lg text-[#EFE6D0]/90 max-w-3xl leading-relaxed">
            From buyer inquiry to container dispatch through suitable Indian ports and post-shipment documentation.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-5xl mx-auto px-4 sm:px-8 space-y-12">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <h2 className="text-3xl font-bold text-[#183D2F]">8-Step B2B Export Workflow</h2>
          <p className="text-sm text-[#4B5563]">Transparent, compliance-aware, and structured for global feed millers and traders.</p>
        </div>

        <div className="space-y-6">
          {exportProcessSteps.map((step) => (
            <div key={step.step} className="bg-white p-8 rounded-2xl border border-[#D8DDD5] shadow-sm flex flex-col md:flex-row gap-6 items-start">
              <div className="bg-[#183D2F] text-[#C6922E] font-extrabold text-xl w-14 h-14 rounded-xl flex items-center justify-center shrink-0">
                0{step.step}
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-[#183D2F]">{step.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Incoterms & Ports */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8">
          <div className="bg-white p-8 rounded-2xl border border-[#D8DDD5] shadow-sm space-y-4">
            <h3 className="font-bold text-xl text-[#183D2F]">Incoterms Supported</h3>
            <ul className="space-y-2.5 text-sm text-[#4B5563]">
              <li className="flex items-center gap-2">✓ <strong>FOB Indian Port:</strong> Mundra, Kandla, JNPT, Pipavav, Hazira</li>
              <li className="flex items-center gap-2">✓ <strong>CFR:</strong> Available for global destination ports across Asia, Middle East & Europe</li>
              <li className="flex items-center gap-2">✓ <strong>CIF:</strong> Available if commercially agreed upon contract terms</li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-[#D8DDD5] shadow-sm space-y-4">
            <h3 className="font-bold text-xl text-[#183D2F]">Port Logistics & Trust Points</h3>
            <ul className="space-y-2.5 text-sm text-[#4B5563]">
              <li className="flex items-center gap-2">✓ Multi-port dispatch flexibility depending on routing</li>
              <li className="flex items-center gap-2">✓ Documentation discipline & batch-wise COA coordination</li>
              <li className="flex items-center gap-2">✓ Container stuffing photos & third-party inspection support</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

// Markets Page
export function MarketsPage() {
  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      <section className="bg-[#183D2F] text-white py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#2F5D46] px-3 py-1 rounded text-xs font-semibold text-[#EFE6D0]">
            Global Footprint & Trade Corridors
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Markets We Serve</h1>
          <p className="text-base sm:text-lg text-[#EFE6D0]/90 max-w-3xl leading-relaxed">
            Serving global feed mills, commodity importers, trading companies, and oilseed meal distributors across worldwide import destinations.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-8 space-y-12">
        {/* Global Reach Highlight */}
        <div className="bg-[#183D2F] text-white p-8 sm:p-12 rounded-2xl shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 space-y-4">
            <span className="bg-[#C6922E] text-[#183D2F] font-bold text-xs px-3 py-1 rounded uppercase tracking-widest">
              Global B2B Agri-Commodity Supply
            </span>
            <h2 className="text-3xl font-bold">Serving International Feed Mills & Importers</h2>
            <p className="text-sm sm:text-base text-[#EFE6D0]/90 leading-relaxed">
              Axora PS Global LLP supplies quality-verified rapeseed/mustard de-oiled cake (DOC) and oilseed meals to global buyers. We coordinate secure containerized shipments through major Indian ports including Mundra, Kandla, Nhava Sheva (JNPT), Pipavav, and Hazira.
            </p>
            <div className="pt-2 flex flex-wrap gap-2">
              {["Global Feed Mills", "Commodity Importers", "Trading Companies", "Animal Feed Manufacturers", "Oilseed Meal Distributors"].map((b, i) => (
                <span key={i} className="bg-[#2F5D46] text-[#EFE6D0] text-xs px-3 py-1 rounded border border-[#C6922E]/30">
                  {b}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 bg-[#143226] p-6 rounded-xl border border-[#2F5D46] space-y-3">
            <h3 className="font-bold text-lg text-[#C6922E]">Why Global Buyers Choose Us</h3>
            <ul className="space-y-2 text-xs text-[#EFE6D0]/90">
              <li>✓ Consistent 36-38% crude protein specs</li>
              <li>✓ Batch-wise COA verification</li>
              <li>✓ Standard 20ft container stuffing</li>
              <li>✓ Flexible port dispatch options</li>
            </ul>
          </div>
        </div>

        {/* Other Markets Grid */}
        <div>
          <h2 className="text-2xl font-bold text-[#183D2F] mb-8">Export Markets & Inquiries</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {marketsServed.map((m, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-4 flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-xl text-[#183D2F]">{m.country}</h3>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded ${
                      m.status.includes('Key') ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {m.status}
                    </span>
                  </div>
                  <p className="text-xs text-[#4B5563] leading-relaxed">{m.description}</p>
                  <div className="pt-2">
                    <p className="text-xs font-semibold text-[#183D2F] mb-1">Target Buyer Segments:</p>
                    <div className="flex flex-wrap gap-1">
                      {m.buyerTypes.map((t, i) => (
                        <span key={i} className="bg-[#F8F7F2] text-[#4B5563] text-[10px] px-2 py-0.5 rounded border border-[#D8DDD5]">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t border-[#D8DDD5]">
                  <Link
                    to="/contact"
                    className="block text-center text-xs font-semibold text-[#183D2F] hover:text-[#C6922E]"
                  >
                    Send Trade Inquiry →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// Certifications and Compliance Page
export function CompliancePage() {
  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      <section className="bg-[#183D2F] text-white py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#2F5D46] px-3 py-1 rounded text-xs font-semibold text-[#EFE6D0]">
            Axora PS Global LLP • GST: {companyInfo.gstNumber}
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Certifications & Compliance</h1>
          <p className="text-base sm:text-lg text-[#EFE6D0]/90 max-w-3xl leading-relaxed">
            Transparent compliance architecture, GST registration, and documentation for international B2B buyers.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-8 space-y-12">
        <div className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-[#C6922E] shrink-0 mt-1" />
          <div className="space-y-1 text-sm text-[#4B5563]">
            <h2 className="font-bold text-[#183D2F]">Compliance & GST Notice</h2>
            <p>
              Axora PS Global LLP is officially registered in Jaipur, Rajasthan. GST Number: <strong>{companyInfo.gstNumber}</strong>. Certification numbers and copies can be shared with qualified buyers where applicable. Product-specific documentation depends on destination requirements, buyer specification, final contract terms, and current export/import regulations.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {complianceList.map((item, idx) => (
            <div key={idx} className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-3 flex flex-col justify-between">
              <div className="space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <h3 className="font-bold text-lg text-[#183D2F]">{item.title}</h3>
                  <span className="text-[10px] font-bold bg-[#183D2F]/10 text-[#183D2F] px-2 py-0.5 rounded shrink-0">
                    {item.status}
                  </span>
                </div>
                <p className="text-xs text-[#4B5563] leading-relaxed">{item.desc}</p>
              </div>
              <div className="pt-3 border-t border-[#D8DDD5] text-xs text-[#C6922E] font-medium flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Verified on Inquiry
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// Contact / Request a Quote Page
export function ContactPage() {
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [formData, setFormData] = React.useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    country: '',
    destinationPort: '',
    productInterest: 'Rapeseed/Mustard DOC',
    gradeRequired: 'Standard Rapeseed/Mustard DOC',
    quantityMT: '22',
    shipmentTerms: 'FOB Indian Port',
    targetMonth: '',
    message: '',
    wantCOA: true,
    agreeContact: true
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, sourcePage: 'contact-page' })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
          : (typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
        setError(msg);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      console.error("Inquiry submission error:", err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      <section className="bg-[#183D2F] text-white py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#2F5D46] px-3 py-1 rounded text-xs font-semibold text-[#EFE6D0]">
            Axora PS Global LLP • GST: {companyInfo.gstNumber}
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Request a Quote & Contact</h1>
          <p className="text-base sm:text-lg text-[#EFE6D0]/90 max-w-3xl leading-relaxed">
            Connect with Puneet Aggarwal and Sushant Lohia for export quotes, port dispatch options, and COA verification.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Contact Details Column */}
        <div className="lg:col-span-5 space-y-8">
          <div className="bg-white p-8 rounded-2xl border border-[#D8DDD5] shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-[#183D2F] border-b border-[#D8DDD5] pb-3">Corporate Headquarters</h2>
            
            <div className="space-y-4 text-sm text-[#4B5563]">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C6922E] shrink-0 mt-0.5" />
                <div>
                  <strong className="text-[#183D2F] block mb-1">Axora PS Global LLP</strong>
                  <span>{companyInfo.address}</span>
                  <p className="text-xs text-[#C6922E] font-semibold mt-1">GST: {companyInfo.gstNumber}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#C6922E] shrink-0" />
                <div>
                  <strong className="text-[#183D2F] block mb-1">Email Inquiry</strong>
                  <a href={`mailto:${companyInfo.email}`} className="text-[#183D2F] font-semibold hover:underline">{companyInfo.email}</a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#C6922E] shrink-0" />
                <div>
                  <strong className="text-[#183D2F] block mb-1">Business Hours</strong>
                  <span>{companyInfo.businessHours}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-[#D8DDD5] shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-[#183D2F] border-b border-[#D8DDD5] pb-3">Co-founders & WhatsApp</h2>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <p className="font-bold text-[#183D2F]">Puneet Aggarwal <span className="text-xs font-normal text-[#4B5563]">(Co-founder)</span></p>
                <div className="flex flex-wrap gap-3">
                  <a href={`tel:${companyInfo.founders[0].phone}`} data-testid="call-puneet-btn" className="bg-[#183D2F] text-white text-xs font-semibold px-4 py-2 rounded flex items-center gap-1.5 hover:bg-[#143226]">
                    <Phone className="w-3.5 h-3.5 text-[#C6922E]" /> {companyInfo.founders[0].phone}
                  </a>
                </div>
              </div>

              <div className="space-y-2">
                <p className="font-bold text-[#183D2F]">Sushant Lohia <span className="text-xs font-normal text-[#4B5563]">(Co-founder)</span></p>
                <div className="flex flex-wrap gap-3">
                  <a href={`tel:${companyInfo.founders[1].phone}`} data-testid="call-sushant-btn" className="bg-[#183D2F] text-white text-xs font-semibold px-4 py-2 rounded flex items-center gap-1.5 hover:bg-[#143226]">
                    <Phone className="w-3.5 h-3.5 text-[#C6922E]" /> {companyInfo.founders[1].phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quote Form Column */}
        <div className="lg:col-span-7">
          <div className="bg-white p-8 sm:p-10 rounded-2xl border border-[#D8DDD5] shadow-sm">
            {submitted ? (
              <div className="text-center py-16 space-y-6" data-testid="quote-success-message">
                <div className="w-16 h-16 bg-green-100 text-green-700 rounded-full flex items-center justify-center mx-auto text-2xl font-bold">
                  ✓
                </div>
                <h3 className="text-2xl font-bold text-[#183D2F]">Inquiry Submitted Successfully</h3>
                <p className="text-sm text-[#4B5563] max-w-md mx-auto leading-relaxed">
                  Thank you. Our export team at Axora PS Global LLP will review your requirement and respond shortly via email or WhatsApp.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="bg-[#183D2F] text-white font-semibold px-6 py-2.5 rounded text-sm hover:bg-[#143226]"
                >
                  Submit Another Inquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-request-form">
                <div className="border-b border-[#D8DDD5] pb-4">
                  <h2 className="text-2xl font-bold text-[#183D2F]">Export Quotation Request</h2>
                  <p className="text-xs text-[#4B5563] mt-1">Please provide your specification and destination port details below.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="e.g. John Smith"
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-fullname"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Company Name *</label>
                    <input
                      type="text"
                      name="companyName"
                      required
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="e.g. Global Feed Mills Ltd"
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="sales@axorapsglobal.com"
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Phone / WhatsApp</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+..."
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Country *</label>
                    <input
                      type="text"
                      name="country"
                      required
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="e.g. Vietnam / UAE / Turkey"
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-country"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Destination Port *</label>
                    <input
                      type="text"
                      name="destinationPort"
                      required
                      value={formData.destinationPort}
                      onChange={handleChange}
                      placeholder="e.g. Haiphong / Jebel Ali"
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-port"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Product Interest</label>
                    <select
                      name="productInterest"
                      value={formData.productInterest}
                      onChange={handleChange}
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm bg-white focus:outline-none focus:border-[#183D2F]"
                      data-testid="select-product"
                    >
                      <option value="Rapeseed/Mustard DOC">Rapeseed/Mustard De-Oiled Cake (DOC)</option>
                      <option value="Expeller Mustard/Rapeseed Cake">Expeller Rapeseed/Mustard Cake</option>
                      <option value="Soybean Meal">Soybean Meal (Coming Soon)</option>
                      <option value="Other Commodity">Other Agri-Commodity</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Grade Required</label>
                    <input
                      type="text"
                      name="gradeRequired"
                      value={formData.gradeRequired}
                      onChange={handleChange}
                      placeholder="Standard / High Protein"
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-grade"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Quantity (MT)</label>
                    <input
                      type="text"
                      name="quantityMT"
                      value={formData.quantityMT}
                      onChange={handleChange}
                      placeholder="22-44 MT"
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-quantity"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Shipment Terms</label>
                    <select
                      name="shipmentTerms"
                      value={formData.shipmentTerms}
                      onChange={handleChange}
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm bg-white focus:outline-none focus:border-[#183D2F]"
                      data-testid="select-terms"
                    >
                      <option value="FOB Indian Port">FOB Indian Port (Mundra/Kandla/JNPT/Pipavav/Hazira)</option>
                      <option value="FOB Mundra">FOB Mundra</option>
                      <option value="FOB Kandla">FOB Kandla / Deendayal</option>
                      <option value="FOB JNPT">FOB Nhava Sheva / JNPT</option>
                      <option value="CFR">CFR (Destination Port)</option>
                      <option value="CIF">CIF (Destination Port)</option>
                      <option value="Not Sure">Not Sure / Negotiable</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Target Month</label>
                    <input
                      type="text"
                      name="targetMonth"
                      value={formData.targetMonth}
                      onChange={handleChange}
                      placeholder="e.g. Q3 2026"
                      className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                      data-testid="input-month"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Additional Message / Specifications</label>
                  <textarea
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Mention any specific protein requirements, lab testing, bag markings, or packing preferences..."
                    className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                    data-testid="textarea-message"
                  ></textarea>
                </div>

                <div className="space-y-2 text-xs text-[#4B5563]">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="wantCOA"
                      checked={formData.wantCOA}
                      onChange={handleChange}
                      className="rounded text-[#183D2F]"
                      data-testid="checkbox-coa"
                    />
                    <span>I would like COA / specification test details</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="agreeContact"
                      checked={formData.agreeContact}
                      onChange={handleChange}
                      className="rounded text-[#183D2F]"
                      data-testid="checkbox-agree"
                    />
                    <span>I agree to be contacted regarding this export inquiry</span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-quote-btn"
                  className="w-full bg-[#183D2F] hover:bg-[#143226] text-white font-bold py-4 rounded-lg shadow-lg text-base transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Submitting Inquiry..." : "Submit Export Quote Request"}
                </button>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm" data-testid="quote-error-message">
                    {error}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// Admin CMS - Products Editor
function ProductsEditor({ authedFetch }) {
  const [products, setProducts] = React.useState([]);
  const [selectedSlug, setSelectedSlug] = React.useState('');
  const [draft, setDraft] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState('');

  const loadProducts = React.useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await authedFetch('/admin/products');
      const data = await res.json();
      setProducts(data);
      if (!selectedSlug && data.length) {
        setSelectedSlug(data[0].slug);
        setDraft(JSON.parse(JSON.stringify(data[0])));
      }
    } catch (err) {
      setErrorMsg(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, selectedSlug]);

  React.useEffect(() => { loadProducts(); }, [loadProducts]);

  const selectProduct = (slug) => {
    const p = products.find(x => x.slug === slug);
    if (p) {
      setSelectedSlug(slug);
      setDraft(JSON.parse(JSON.stringify(p)));
      setMessage('');
      setErrorMsg('');
    }
  };

  const updateField = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  const updateSpec = (idx, key, value) => {
    setDraft(prev => {
      const specs = [...(prev.specifications || [])];
      specs[idx] = { ...specs[idx], [key]: value };
      return { ...prev, specifications: specs };
    });
  };

  const addSpec = () => setDraft(prev => ({
    ...prev,
    specifications: [...(prev.specifications || []), { parameter: '', typical: '', notes: '' }],
  }));

  const removeSpec = (idx) => setDraft(prev => ({
    ...prev,
    specifications: (prev.specifications || []).filter((_, i) => i !== idx),
  }));

  const updateList = (field, idx, value) => {
    setDraft(prev => {
      const arr = [...(prev[field] || [])];
      arr[idx] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addToList = (field) => setDraft(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  const removeFromList = (field, idx) => setDraft(prev => ({
    ...prev,
    [field]: (prev[field] || []).filter((_, i) => i !== idx),
  }));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setMessage('');
    setErrorMsg('');
    try {
      const payload = {
        name: draft.name,
        subtitle: draft.subtitle,
        origin: draft.origin,
        exportPort: draft.exportPort,
        primaryMarket: draft.primaryMarket,
        packaging: draft.packaging,
        containerLoad: draft.containerLoad,
        leadTime: draft.leadTime,
        shelfLife: draft.shelfLife,
        storage: draft.storage,
        grades: (draft.grades || []).filter(x => x && x.trim()),
        tags: (draft.tags || []).filter(x => x && x.trim()),
        specifications: (draft.specifications || []).filter(s => s.parameter && s.parameter.trim()),
        qualityChecks: (draft.qualityChecks || []).filter(x => x && x.trim()),
        documents: (draft.documents || []).filter(x => x && x.trim()),
      };
      const res = await authedFetch(`/admin/products/${draft.slug}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Save failed');
      }
      const updated = await res.json();
      setProducts(prev => prev.map(p => p.slug === updated.slug ? updated : p));
      setDraft(updated);
      setMessage('Saved successfully. Public site reflects changes immediately.');
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !draft) return <div className="text-sm text-[#4B5563]" data-testid="products-editor-loading">Loading products...</div>;

  return (
    <div className="space-y-6" data-testid="products-editor">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#183D2F]">Product Specification Management</h2>
          <p className="text-xs text-[#4B5563]">Edit product details and specs. Changes go live on the public site instantly.</p>
        </div>
        <select
          value={selectedSlug}
          onChange={(e) => selectProduct(e.target.value)}
          className="p-2 rounded border border-[#D8DDD5] bg-white text-sm font-semibold"
          data-testid="product-select"
        >
          {products.map(p => (
            <option key={p.slug} value={p.slug}>{p.name}</option>
          ))}
        </select>
      </div>

      {errorMsg && <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded text-sm" data-testid="products-editor-error">{errorMsg}</div>}
      {message && <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded text-sm" data-testid="products-editor-success">{message}</div>}

      {draft && (
        <>
          {/* Basic fields */}
          <div className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-4">
            <h3 className="font-bold text-sm text-[#183D2F] uppercase tracking-wider border-b border-[#D8DDD5] pb-2">Product Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Product Name" value={draft.name} onChange={v => updateField('name', v)} testid="edit-name" />
              <TextField label="Origin" value={draft.origin} onChange={v => updateField('origin', v)} testid="edit-origin" />
              <TextField label="Export Ports" value={draft.exportPort} onChange={v => updateField('exportPort', v)} testid="edit-ports" />
              <TextField label="Primary Market" value={draft.primaryMarket} onChange={v => updateField('primaryMarket', v)} testid="edit-market" />
              <TextField label="Packaging" value={draft.packaging} onChange={v => updateField('packaging', v)} testid="edit-packaging" />
              <TextField label="Container Load" value={draft.containerLoad} onChange={v => updateField('containerLoad', v)} testid="edit-container" />
              <TextField label="Lead Time" value={draft.leadTime} onChange={v => updateField('leadTime', v)} testid="edit-leadtime" />
              <TextField label="Shelf Life" value={draft.shelfLife} onChange={v => updateField('shelfLife', v)} testid="edit-shelf" />
            </div>
            <TextAreaField label="Subtitle" value={draft.subtitle} onChange={v => updateField('subtitle', v)} testid="edit-subtitle" />
            <TextAreaField label="Storage Guidance" value={draft.storage} onChange={v => updateField('storage', v)} testid="edit-storage" />
          </div>

          {/* Specifications */}
          <div className="bg-white p-6 rounded-xl border border-[#D8DDD5] shadow-sm space-y-4">
            <div className="flex justify-between items-center border-b border-[#D8DDD5] pb-2">
              <h3 className="font-bold text-sm text-[#183D2F] uppercase tracking-wider">Indicative Specifications ({(draft.specifications || []).length})</h3>
              <button onClick={addSpec} className="text-xs font-semibold text-[#183D2F] hover:text-[#C6922E]" data-testid="add-spec-btn">+ Add Row</button>
            </div>
            <div className="space-y-2">
              {(draft.specifications || []).map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`spec-row-${i}`}>
                  <input className="col-span-3 p-2 rounded border border-[#D8DDD5] text-xs" placeholder="Parameter" value={s.parameter} onChange={e => updateSpec(i, 'parameter', e.target.value)} data-testid={`spec-parameter-${i}`} />
                  <input className="col-span-4 p-2 rounded border border-[#D8DDD5] text-xs" placeholder="Typical / Indicative Value" value={s.typical} onChange={e => updateSpec(i, 'typical', e.target.value)} data-testid={`spec-typical-${i}`} />
                  <input className="col-span-4 p-2 rounded border border-[#D8DDD5] text-xs" placeholder="Notes" value={s.notes || ''} onChange={e => updateSpec(i, 'notes', e.target.value)} data-testid={`spec-notes-${i}`} />
                  <button onClick={() => removeSpec(i)} className="col-span-1 text-red-600 text-xs font-semibold hover:text-red-800 p-2" data-testid={`spec-remove-${i}`}>Remove</button>
                </div>
              ))}
            </div>
          </div>

          {/* List fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ListEditor label="Grades Available" items={draft.grades} onChange={(i, v) => updateList('grades', i, v)} onAdd={() => addToList('grades')} onRemove={i => removeFromList('grades', i)} testid="grades" />
            <ListEditor label="Tags" items={draft.tags} onChange={(i, v) => updateList('tags', i, v)} onAdd={() => addToList('tags')} onRemove={i => removeFromList('tags', i)} testid="tags" />
            <ListEditor label="Quality Checks" items={draft.qualityChecks} onChange={(i, v) => updateList('qualityChecks', i, v)} onAdd={() => addToList('qualityChecks')} onRemove={i => removeFromList('qualityChecks', i)} testid="checks" />
            <ListEditor label="Documents Available" items={draft.documents} onChange={(i, v) => updateList('documents', i, v)} onAdd={() => addToList('documents')} onRemove={i => removeFromList('documents', i)} testid="docs" />
          </div>

          <div className="flex justify-end gap-3 sticky bottom-4 bg-white/80 backdrop-blur-sm p-3 rounded-xl border border-[#D8DDD5] shadow-lg">
            <button
              onClick={() => selectProduct(selectedSlug)}
              className="bg-white border border-[#D8DDD5] text-[#4B5563] font-semibold px-5 py-2.5 rounded text-sm hover:bg-[#F8F7F2]"
              data-testid="reset-product-btn"
            >
              Reset
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="bg-[#183D2F] hover:bg-[#143226] text-white font-bold px-6 py-2.5 rounded text-sm shadow disabled:opacity-60"
              data-testid="save-product-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-[#183D2F] uppercase tracking-wider mb-1">{label}</span>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full p-2.5 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
        data-testid={testid}
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-[#183D2F] uppercase tracking-wider mb-1">{label}</span>
      <textarea
        rows={3}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full p-2.5 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
        data-testid={testid}
      />
    </label>
  );
}

function ListEditor({ label, items, onChange, onAdd, onRemove, testid }) {
  const list = items || [];
  return (
    <div className="bg-white p-4 rounded-xl border border-[#D8DDD5]" data-testid={`list-${testid}`}>
      <div className="flex justify-between items-center pb-2 mb-2 border-b border-[#D8DDD5]">
        <h4 className="font-bold text-xs text-[#183D2F] uppercase tracking-wider">{label} ({list.length})</h4>
        <button onClick={onAdd} className="text-xs font-semibold text-[#183D2F] hover:text-[#C6922E]" data-testid={`list-${testid}-add`}>+ Add</button>
      </div>
      <div className="space-y-1.5">
        {list.map((v, i) => (
          <div key={i} className="flex gap-1.5">
            <input
              type="text"
              value={v}
              onChange={e => onChange(i, e.target.value)}
              className="flex-1 p-2 rounded border border-[#D8DDD5] text-xs"
              data-testid={`list-${testid}-input-${i}`}
            />
            <button onClick={() => onRemove(i)} className="text-red-600 text-xs font-semibold hover:text-red-800 px-2" data-testid={`list-${testid}-remove-${i}`}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Admin CMS & Inquiries Dashboard
export function AdminDashboard() {
  const [token, setToken] = React.useState(() => localStorage.getItem('axora_admin_token') || '');
  const [authError, setAuthError] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const [inquiries, setInquiries] = React.useState([]);
  const [stats, setStats] = React.useState({ total: 0, new: 0, contacted: 0, quoted: 0, closed: 0 });
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [fetchError, setFetchError] = React.useState('');
  const [inquiriesLoading, setInquiriesLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('inquiries');

  const authed = Boolean(token);

  const authedFetch = React.useCallback(async (path, options = {}) => {
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}), Authorization: `Bearer ${token}` };
    const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    if (res.status === 401) {
      localStorage.removeItem('axora_admin_token');
      setToken('');
      throw new Error('Session expired. Please log in again.');
    }
    return res;
  }, [token]);

  const fetchInquiries = React.useCallback(async (filter = statusFilter) => {
    if (!token) return;
    setInquiriesLoading(true);
    setFetchError('');
    try {
      const url = filter && filter !== 'all' ? `/admin/inquiries?status=${encodeURIComponent(filter)}` : '/admin/inquiries';
      const [invRes, statsRes] = await Promise.all([
        authedFetch(url),
        authedFetch('/admin/inquiries/stats'),
      ]);
      if (invRes.ok) setInquiries(await invRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      setFetchError(err.message || 'Failed to load inquiries');
    } finally {
      setInquiriesLoading(false);
    }
  }, [token, statusFilter, authedFetch]);

  React.useEffect(() => {
    if (authed) fetchInquiries(statusFilter);
  }, [authed, statusFilter, fetchInquiries]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        setAuthError(typeof detail === 'string' ? detail : 'Login failed. Check credentials.');
        return;
      }
      localStorage.setItem('axora_admin_token', data.access_token);
      setToken(data.access_token);
      setPassword('');
    } catch (err) {
      setAuthError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('axora_admin_token');
    setToken('');
    setInquiries([]);
  };

  const updateStatus = async (id, newStatus) => {
    const prev = inquiries;
    setInquiries(list => list.map(i => i.id === id ? { ...i, status: newStatus } : i));
    try {
      const res = await authedFetch(`/admin/inquiries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Update failed');
      fetchInquiries(statusFilter);
    } catch (err) {
      setInquiries(prev);
      setFetchError(err.message || 'Failed to update status');
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      const res = await authedFetch(`/admin/inquiries/${id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error('Delete failed');
      fetchInquiries(statusFilter);
    } catch (err) {
      setFetchError(err.message || 'Failed to delete inquiry');
    }
  };

  const downloadCSV = () => {
    const url = `${API_BASE}/admin/inquiries.csv?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F8F7F2] flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl border border-[#D8DDD5] shadow-lg max-w-md w-full space-y-6">
          <div className="text-center space-y-2">
            <div className="w-12 h-12 bg-[#183D2F] text-[#C6922E] font-bold rounded-lg flex items-center justify-center mx-auto text-xl">
              APG
            </div>
            <h1 className="text-2xl font-bold text-[#183D2F]">Admin Portal Login</h1>
            <p className="text-xs text-[#4B5563]">Axora PS Global LLP Export Management System</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Admin Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@axorapsglobal.com"
                autoComplete="username"
                className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#183D2F] uppercase mb-1">Admin Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                className="w-full p-3 rounded border border-[#D8DDD5] text-sm focus:outline-none focus:border-[#183D2F]"
                data-testid="admin-password-input"
              />
            </div>
            {authError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-xs" data-testid="admin-login-error">
                {authError}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full bg-[#183D2F] text-white font-bold py-3 rounded text-sm hover:bg-[#143226] disabled:opacity-60"
            >
              {loading ? 'Signing in...' : 'Access Admin Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1F2933]">
      <header className="bg-[#183D2F] text-white py-4 px-8 flex justify-between items-center border-b border-[#2F5D46]">
        <div className="flex items-center gap-3">
          <div className="bg-[#C6922E] text-[#183D2F] font-bold p-2 rounded text-sm">
            APG
          </div>
          <div>
            <h1 className="font-bold text-lg">Admin & CMS Dashboard</h1>
            <p className="text-xs text-[#EFE6D0]/85">Axora PS Global LLP • Jaipur Operations</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleLogout}
            className="bg-[#2F5D46] hover:bg-[#143226] text-white px-3 py-1.5 rounded text-xs font-semibold"
            data-testid="admin-logout-btn"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8">
        {/* Nav Tabs */}
        <div className="flex flex-wrap gap-3 border-b border-[#D8DDD5] pb-4">
          <button
            onClick={() => setActiveTab('inquiries')}
            className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
              activeTab === 'inquiries' ? 'bg-[#183D2F] text-white' : 'bg-white text-[#4B5563] border border-[#D8DDD5]'
            }`}
            data-testid="admin-tab-inquiries"
          >
            Buyer Inquiries ({stats.total})
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
              activeTab === 'products' ? 'bg-[#183D2F] text-white' : 'bg-white text-[#4B5563] border border-[#D8DDD5]'
            }`}
            data-testid="admin-tab-products"
          >
            Product Specs Management
          </button>
          <button
            onClick={() => setActiveTab('compliance')}
            className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
              activeTab === 'compliance' ? 'bg-[#183D2F] text-white' : 'bg-white text-[#4B5563] border border-[#D8DDD5]'
            }`}
            data-testid="admin-tab-compliance"
          >
            Compliance & GST Status
          </button>
        </div>

        {/* Tab 1: Inquiries */}
        {activeTab === 'inquiries' && (
          <div className="space-y-6">
            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="inquiry-stats">
              {[
                { label: 'Total', value: stats.total, color: 'bg-[#183D2F] text-white' },
                { label: 'New', value: stats.new, color: 'bg-blue-100 text-blue-800' },
                { label: 'Contacted', value: stats.contacted, color: 'bg-amber-100 text-amber-800' },
                { label: 'Quoted', value: stats.quoted, color: 'bg-purple-100 text-purple-800' },
                { label: 'Closed', value: stats.closed, color: 'bg-green-100 text-green-800' },
              ].map((s) => (
                <div key={s.label} className={`p-4 rounded-lg ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>
                  <p className="text-xs font-semibold uppercase opacity-80">{s.label}</p>
                  <p className="text-2xl font-extrabold">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-between items-center gap-3">
              <div>
                <h2 className="text-xl font-bold text-[#183D2F]">Buyer Quote Inquiries</h2>
                <p className="text-xs text-[#4B5563]">Live data from backend • Manage international procurement requests.</p>
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="p-2 rounded text-xs border border-[#D8DDD5] bg-white font-semibold"
                  data-testid="filter-status"
                >
                  <option value="all">All Statuses</option>
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Quoted">Quoted</option>
                  <option value="Closed">Closed</option>
                </select>
                <button
                  onClick={() => fetchInquiries(statusFilter)}
                  className="bg-white border border-[#D8DDD5] text-[#183D2F] font-semibold px-3 py-2 rounded text-xs"
                  data-testid="refresh-inquiries-btn"
                >
                  Refresh
                </button>
                <button
                  onClick={downloadCSV}
                  data-testid="export-csv-btn"
                  className="bg-[#C6922E] hover:bg-[#b08026] text-[#183D2F] font-semibold px-4 py-2 rounded text-xs flex items-center gap-2 shadow"
                >
                  <FileSpreadsheet className="w-4 h-4" /> Export CSV
                </button>
              </div>
            </div>

            {fetchError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">{fetchError}</div>
            )}

            <div className="bg-white rounded-xl border border-[#D8DDD5] shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-[#F8F7F2] text-[#183D2F] border-b border-[#D8DDD5]">
                      <th className="p-3">Time (UTC)</th>
                      <th className="p-3">Buyer & Company</th>
                      <th className="p-3">Contact</th>
                      <th className="p-3">Destination</th>
                      <th className="p-3">Product / Qty</th>
                      <th className="p-3">Terms</th>
                      <th className="p-3">Status</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D8DDD5]">
                    {inquiriesLoading && (
                      <tr><td colSpan={8} className="p-6 text-center text-[#4B5563]">Loading inquiries...</td></tr>
                    )}
                    {!inquiriesLoading && inquiries.length === 0 && (
                      <tr><td colSpan={8} className="p-6 text-center text-[#4B5563]" data-testid="no-inquiries">
                        No inquiries yet. Submissions from the contact form will appear here.
                      </td></tr>
                    )}
                    {inquiries.map(item => (
                      <tr key={item.id} className="hover:bg-[#F8F7F2]/50" data-testid={`inquiry-row-${item.id}`}>
                        <td className="p-3 text-[#4B5563] whitespace-nowrap">{item.timestamp}</td>
                        <td className="p-3">
                          <p className="font-bold text-[#183D2F]">{item.fullName}</p>
                          <p className="text-[11px] text-[#4B5563]">{item.companyName}</p>
                        </td>
                        <td className="p-3">
                          <p><a href={`mailto:${item.email}`} className="text-[#183D2F] hover:underline">{item.email}</a></p>
                          <p className="text-[11px] text-[#4B5563]">{item.phone}</p>
                        </td>
                        <td className="p-3">
                          <p className="font-semibold">{item.country}</p>
                          <p className="text-[11px] text-[#4B5563]">{item.destinationPort}</p>
                        </td>
                        <td className="p-3">
                          <p className="font-semibold">{item.productInterest}</p>
                          <p className="text-[11px] text-[#4B5563]">{item.quantityMT} MT</p>
                        </td>
                        <td className="p-3 font-medium">{item.shipmentTerms}</td>
                        <td className="p-3">
                          <select
                            value={item.status}
                            onChange={(e) => updateStatus(item.id, e.target.value)}
                            className="p-1 rounded text-xs border border-[#D8DDD5] bg-white font-semibold"
                            data-testid={`status-select-${item.id}`}
                          >
                            <option value="New">New</option>
                            <option value="Contacted">Contacted</option>
                            <option value="Quoted">Quoted</option>
                            <option value="Closed">Closed</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => deleteInquiry(item.id)}
                            className="text-red-600 hover:text-red-800 text-[11px] font-semibold"
                            data-testid={`delete-inquiry-${item.id}`}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Products CMS */}
        {activeTab === 'products' && (
          <ProductsEditor authedFetch={authedFetch} />
        )}

        {/* Tab 3: Compliance Status */}
        {activeTab === 'compliance' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-[#183D2F]">Compliance & GST Status Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {complianceList.map((c, i) => (
                <div key={i} className="bg-white p-4 rounded-xl border border-[#D8DDD5] flex justify-between items-center text-xs">
                  <div>
                    <p className="font-bold text-[#183D2F] text-sm">{c.title}</p>
                    <p className="text-[#4B5563]">{c.desc}</p>
                  </div>
                  <span className="bg-green-100 text-green-800 font-bold px-2 py-1 rounded">
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Main App Export
export default function App() {
  return (
    <Router>
      <div className="min-h-screen flex flex-col font-sans bg-[#F8F7F2] text-[#1F2933]">
        <Navbar />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/rapeseed-mustard-de-oiled-cake" element={<MustardDocDetailPage />} />
            <Route path="/export-process" element={<ExportProcessPage />} />
            <Route path="/markets" element={<MarketsPage />} />
            <Route path="/certifications-compliance" element={<CompliancePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}
